import { useState } from 'react';
import { Campaign } from '../types';
import { useMarketingData } from '../MarketingContext';

export default function CampaignsPage() {
  const { companies, projects, campaigns, updateCampaign } = useMarketingData();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState<Campaign | null>(null);
  const [view, setView] = useState<'list' | 'kanban'>('list');

  const filtered = campaigns.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statuses = ['idea', 'planned', 'in_progress', 'paused', 'completed', 'cancelled'];
  const statusColors: Record<string, string> = {
    idea: 'bg-gray-100 text-gray-600', planned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-green-100 text-green-700', paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-700',
  };

  if (selected) {
    const co = companies.find(c => c.id === selected.companyId);
    const proj = projects.find(p => p.id === selected.projectId);
    return (
      <div className="space-y-4">
        <button onClick={() => setSelected(null)} className="text-sm text-blue-600 hover:underline">← Back to Campaigns</button>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selected.name}</h2>
              <p className="text-sm text-gray-500 mt-1">{selected.objective}</p>
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[selected.status]}`}>{selected.status}</span>
                <span className="text-xs text-gray-400">🏢 {co?.name} · 📋 {proj?.name}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-xl p-4"><div className="text-xs text-blue-600 font-medium">Budget</div><div className="text-xl font-bold text-blue-800">₹{(selected.budget / 1000).toFixed(0)}K</div></div>
            <div className="bg-green-50 rounded-xl p-4"><div className="text-xs text-green-600 font-medium">Leads</div><div className="text-xl font-bold text-green-800">{selected.leads}</div></div>
            <div className="bg-purple-50 rounded-xl p-4"><div className="text-xs text-purple-600 font-medium">Conversions</div><div className="text-xl font-bold text-purple-800">{selected.conversions}</div></div>
            <div className="bg-amber-50 rounded-xl p-4"><div className="text-xs text-amber-600 font-medium">Revenue</div><div className="text-xl font-bold text-amber-800">₹{(selected.revenue / 1000).toFixed(0)}K</div></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Details</h3>
              <div className="text-xs space-y-1.5">
                <div><span className="text-gray-500">Type:</span> <span className="text-gray-800">{selected.type}</span></div>
                <div><span className="text-gray-500">Target Audience:</span> <span className="text-gray-800">{selected.targetAudience}</span></div>
                <div><span className="text-gray-500">Persona:</span> <span className="text-gray-800">{selected.persona}</span></div>
                <div><span className="text-gray-500">Funnel Stage:</span> <span className="text-gray-800">{selected.funnelStage}</span></div>
                <div><span className="text-gray-500">Channels:</span> <span className="text-gray-800">{selected.channels.join(', ')}</span></div>
                <div><span className="text-gray-500">Timeline:</span> <span className="text-gray-800">{selected.startDate} → {selected.endDate}</span></div>
                <div><span className="text-gray-500">Owner:</span> <span className="text-gray-800">{selected.owner}</span></div>
                <div><span className="text-gray-500">Team:</span> <span className="text-gray-800">{selected.team.join(', ')}</span></div>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Goals & Results</h3>
              <div className="text-xs space-y-1.5">
                <div><span className="text-gray-500">Conversion Goal:</span> <span className="text-gray-800">{selected.conversionGoal}</span></div>
                <div><span className="text-gray-500">Expected:</span> <span className="text-gray-800">{selected.expectedResults}</span></div>
                <div><span className="text-gray-500">Actual:</span> <span className="text-gray-800">{selected.actualResults || 'Not yet measured'}</span></div>
                <div><span className="text-gray-500">ROI:</span> <span className="text-gray-800">{selected.roi > 0 ? `${selected.roi}x` : 'Not yet calculated'}</span></div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Status Actions</h3>
                <div className="flex flex-wrap gap-1.5">
                  {statuses.filter(s => s !== selected.status).slice(0, 4).map(s => (
                    <button key={s} onClick={() => { updateCampaign(selected.id, { status: s as any }); setSelected({ ...selected, status: s as any }); }} className={`text-[10px] px-2 py-1 rounded-full ${statusColors[s]} hover:opacity-80`}>→ {s}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {selected.notes && <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600"><strong>Notes:</strong> {selected.notes}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">Campaigns</h1><p className="text-sm text-gray-500">Manage marketing campaigns across all companies</p></div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setView('list')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>List</button>
          <button onClick={() => setView('kanban')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'kanban' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>Kanban</button>
        </div>
      </div>

      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search campaigns..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Status</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {view === 'list' ? (
        <div className="space-y-3">
          {filtered.map(c => {
            const co = companies.find(comp => comp.id === c.companyId);
            return (
              <button key={c.id} onClick={() => setSelected(c)} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-gray-900">{c.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{c.objective.slice(0, 100)}...</div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                      <span>🏢 {co?.name}</span><span>💰 ₹{(c.budget / 1000).toFixed(0)}K</span><span>📅 {c.startDate} → {c.endDate}</span><span>{c.channels.join(', ')}</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[c.status]}`}>{c.status}</span>
                    <div className="text-[10px] text-gray-400 mt-1">{c.leads} leads · {c.conversions} conv</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statuses.map(status => (
            <div key={status} className="bg-gray-50 rounded-xl p-3">
              <h3 className="text-xs font-bold text-gray-600 mb-2 capitalize">{status.replace('_', ' ')} ({filtered.filter(c => c.status === status).length})</h3>
              <div className="space-y-2">
                {filtered.filter(c => c.status === status).map(c => (
                  <button key={c.id} onClick={() => setSelected(c)} className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-left hover:shadow-sm">
                    <div className="text-xs font-medium text-gray-800">{c.name}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">₹{(c.budget / 1000).toFixed(0)}K</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
